import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { MessageSquarePlus, Send, Users as UsersIcon } from "lucide-react";
import { initials } from "@clinic/ui";
import { ROLE_LABELS, type Conversation } from "@clinic/types";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function ChatPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const conversations = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: () => api.chat.conversations(),
    refetchInterval: 4000,
  });

  const active = conversations.data?.find((c) => c.id === activeId) ?? null;

  return (
    <div className="flex h-[calc(100vh-9rem)] flex-col">
      <PageHeader
        title="Chat"
        subtitle="Private staff messaging — direct and group conversations"
        actions={
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <MessageSquarePlus size={15} /> New chat
          </Button>
        }
      />

      <div
        className="flex min-h-0 flex-1 overflow-hidden rounded-card border bg-bg-secondary"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        {/* conversation list */}
        <div
          className="flex w-72 shrink-0 flex-col border-r"
          style={{ borderColor: "var(--accent-subtle)" }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {conversations.isLoading ? (
              <div className="space-y-2 p-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14" />
                ))}
              </div>
            ) : conversations.data && conversations.data.length > 0 ? (
              conversations.data.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  active={c.id === activeId}
                  onSelect={() => setActiveId(c.id)}
                />
              ))
            ) : (
              <p className="px-3 py-8 text-center text-sm text-text-tertiary">
                No conversations yet. Start one with “New chat”.
              </p>
            )}
          </div>
        </div>

        {/* thread */}
        <div className="flex min-w-0 flex-1 flex-col">
          {active ? (
            <Thread conversation={active} meId={me.data?.id ?? ""} />
          ) : (
            <div className="flex flex-1 items-center justify-center p-8 text-center text-text-tertiary">
              Select a conversation, or start a new one.
            </div>
          )}
        </div>
      </div>

      <NewChatDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(conv) => {
          queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
          setActiveId(conv.id);
          setNewOpen(false);
        }}
      />
    </div>
  );
}

function ConversationRow({
  conversation,
  active,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  onSelect: () => void;
}) {
  const last = conversation.lastMessage;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition",
        active ? "bg-accent-subtle" : "hover:bg-bg-tertiary",
      )}
    >
      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-accent-subtle text-xs font-semibold text-accent-light">
        {conversation.isGroup ? <UsersIcon size={16} /> : initials(conversation.title)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{conversation.title}</span>
          {last && (
            <span className="shrink-0 text-[11px] text-text-tertiary">
              {formatDistanceToNowStrict(new Date(last.createdAt))}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-text-tertiary">
            {last ? last.body : "No messages yet"}
          </span>
          {conversation.unreadCount > 0 && (
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-pill bg-accent px-1 text-[10px] font-semibold text-on-accent">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function Thread({ conversation, meId }: { conversation: Conversation; meId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = useQuery({
    queryKey: ["chat", "messages", conversation.id],
    queryFn: () => api.chat.messages(conversation.id),
    refetchInterval: 3000,
  });

  const send = useMutation({
    mutationFn: (body: string) => api.chat.send(conversation.id, { body }),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["chat", "messages", conversation.id] });
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      queryClient.invalidateQueries({ queryKey: ["chat", "unread"] });
    },
  });

  // Mark read whenever we open / receive in this thread.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["chat", "unread"] });
    queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id, messages.data?.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.data?.length, conversation.id]);

  const submit = () => {
    const body = draft.trim();
    if (body) send.mutate(body);
  };

  return (
    <>
      <div
        className="flex items-center gap-3 border-b px-5 py-3"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-pill bg-accent-subtle text-xs font-semibold text-accent-light">
          {conversation.isGroup ? <UsersIcon size={16} /> : initials(conversation.title)}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{conversation.title}</div>
          <div className="truncate text-xs text-text-tertiary">
            {conversation.isGroup
              ? conversation.otherMembers.map((m) => m.name).join(", ")
              : ROLE_LABELS[conversation.otherMembers[0]?.role ?? "RECEPTIONIST"]}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {messages.isLoading ? (
          <Skeleton className="h-20" />
        ) : (
          (messages.data ?? []).map((m) => {
            const mine = m.senderId === meId;
            return (
              <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                <div className="max-w-[72%]">
                  {conversation.isGroup && !mine && (
                    <div className="mb-0.5 px-1 text-[11px] font-medium text-accent-light">
                      {m.sender?.name}
                    </div>
                  )}
                  <div
                    className={cn(
                      "rounded-2xl px-3.5 py-2 text-sm",
                      mine
                        ? "rounded-br-sm bg-accent text-on-accent"
                        : "rounded-bl-sm bg-bg-tertiary text-text",
                    )}
                  >
                    <span className="whitespace-pre-wrap break-words">{m.body}</span>
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 px-1 text-[10px] text-text-tertiary",
                      mine ? "text-right" : "text-left",
                    )}
                  >
                    {new Date(m.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {messages.data && messages.data.length === 0 && (
          <p className="py-8 text-center text-sm text-text-tertiary">
            No messages yet — say hello 👋
          </p>
        )}
        <div ref={bottomRef} />
      </div>

      <div
        className="flex items-end gap-2 border-t px-4 py-3"
        style={{ borderColor: "var(--accent-subtle)" }}
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Write a message…  (Enter to send, Shift+Enter for newline)"
          className="min-h-[44px] max-h-32 flex-1 resize-none"
          rows={1}
        />
        <Button onClick={submit} loading={send.isPending} disabled={!draft.trim()} className="h-11">
          <Send size={15} />
        </Button>
      </div>
    </>
  );
}

function NewChatDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (conv: Conversation) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState("");

  const directory = useQuery({
    queryKey: ["directory"],
    queryFn: () => api.users.directory(),
    enabled: open,
  });

  const start = useMutation({
    mutationFn: () =>
      api.chat.start({
        participantIds: selected,
        title: selected.length > 1 ? title.trim() || undefined : undefined,
      }),
    onSuccess: (conv) => {
      setSelected([]);
      setTitle("");
      onCreated(conv);
    },
  });

  const isGroup = selected.length > 1;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New conversation" description="Pick one person for a DM, or several for a group.">
        <div className="space-y-4">
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {directory.isLoading ? (
              <Skeleton className="h-10" />
            ) : (
              (directory.data ?? []).map((u) => (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-bg-tertiary"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(u.id)}
                    onChange={() => toggle(u.id)}
                    className="h-4 w-4 accent-[var(--accent)]"
                  />
                  <div className="flex h-8 w-8 items-center justify-center rounded-pill bg-accent-subtle text-xs font-semibold text-accent-light">
                    {initials(u.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{u.name}</div>
                    <div className="truncate text-xs text-text-tertiary">{ROLE_LABELS[u.role]}</div>
                  </div>
                </label>
              ))
            )}
            {directory.data && directory.data.length === 0 && (
              <p className="py-6 text-center text-sm text-text-tertiary">No other staff yet.</p>
            )}
          </div>

          {isGroup && (
            <div>
              <Label>Group name (optional)</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Front desk"
              />
            </div>
          )}

          {start.isError && (
            <p className="text-sm text-danger">
              {start.error instanceof Error ? start.error.message : "Could not start conversation"}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => start.mutate()}
              disabled={selected.length === 0}
              loading={start.isPending}
            >
              {isGroup ? "Create group" : "Start chat"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
